import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, Landmark, Building2, FileSearch, Hammer, Scale, Eye,
  Users2, Briefcase, LineChart, Handshake, ClipboardCheck, Globe2, MapPin, UserRound, Clock4, ShieldCheck,
} from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSiteConfig } from "@/context/SiteConfigContext";
import { Seo } from "@/lib/seo";
import {
  Reveal, SectionHeading, CTASection, ComplianceNote, ProcessTimeline, stagger, fadeUp,
} from "@/components/shared/Shared";

const IMG_HERO = "https://images.unsplash.com/photo-1579704698215-cfa81bd71b33?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400";

const BEREICHE = [
  {
    icon: Landmark,
    kicker: "A",
    title: "Eleven Family Office",
    text: "Internationale Vermögensstrukturen, Verwaltung und langfristige Begleitung für Unternehmer, Investoren und vermögende Privatpersonen.",
    to: "/family-office",
    cta: "Family Office entdecken",
    testid: "home-card-family-office",
  },
  {
    icon: Building2,
    kicker: "B",
    title: "Eleven Real Estate",
    text: "Geprüfte Bestandswohnungen als langfristige Kapitalanlage – von der Finanzierung bis zur laufenden Betreuung.",
    to: "/real-estate",
    cta: "Real Estate entdecken",
    testid: "home-card-real-estate",
  },
  {
    icon: FileSearch,
    kicker: "C",
    title: "Eleven Maklerservice",
    text: "Bestehende Vorsorgeverträge analysieren, Kosten transparent machen und mögliche Optimierungswege prüfen.",
    to: "/maklerservice",
    cta: "Maklerservice entdecken",
    testid: "home-card-maklerservice",
  },
  {
    icon: Hammer,
    kicker: "D",
    title: "Eleven Development",
    text: "Eigene Projektentwicklung, Sanierung und Objektbetreuung – Qualität und Termine bleiben im Haus.",
    to: "/development",
    cta: "Development entdecken",
    testid: "home-card-development",
  },
];

export default function Home() {
  const site = useSiteConfig();
  const navigate = useNavigate();
  const [plz, setPlz] = useState("");
  const [plzError, setPlzError] = useState(false);

  const goBeraterFinden = () => {
    if (plz && !/^\d{5}$/.test(plz)) {
      setPlzError(true);
      return;
    }
    setPlzError(false);
    navigate(plz ? `/berater-finden?plz=${plz}` : "/berater-finden");
  };

  const prozess = [
    { title: "Erstgespräch", text: "Kostenfrei und unverbindlich: Wir hören zu und verstehen Ihre Situation – bevor über Lösungen gesprochen wird." },
    { title: "Analyse", text: "Vermögensstruktur, Verträge oder Finanzierungsrahmen – je nach Anliegen analysieren wir die Ausgangslage sachlich." },
    { title: "Konzept", text: "Sie erhalten einen dokumentierten, nachvollziehbaren Vorschlag – inklusive Risiken und Grenzen." },
    { title: "Prüfung", text: "Sie entscheiden in Ihrem Tempo – gern gemeinsam mit Steuerberater, Notar oder Familie." },
    { title: "Begleitung", text: "Nach der Umsetzung bleiben wir Ihr Ansprechpartner – über die gesamte Zusammenarbeit." },
  ];

  return (
    <div className="bg-white">
      <Seo
        title="II Eleven Gruppe – Family Office, Real Estate, Maklerservice & Development"
        description="Vermögen strukturieren. Substanz erhalten. Zukunft gestalten. Die II Eleven Gruppe verbindet Vermögensverwaltung, internationale Strukturen, Immobilien und Vorsorgelösungen unter einer gemeinsamen Verantwortung."
      />

      {/* 1. HERO – heller Split-Screen im Stil der Praesentationen */}
      <section className="bg-white" data-testid="home-hero">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 lg:pt-24 pb-14 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-6">
              <motion.div variants={fadeUp} className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6B7280]">II Eleven Gruppe · Stuttgart</p>
              </motion.div>
              <motion.h1 variants={fadeUp} className="mt-6 font-display text-4xl sm:text-5xl lg:text-[60px] font-bold tracking-[-0.02em] leading-[1.04] text-[#0E1116]" data-testid="home-hero-headline">
                Vermögen strukturieren.<br />Substanz erhalten.<br />
                <span className="text-[#FF3C20]">Zukunft gestalten.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="mt-7 text-base md:text-lg text-[#3B4452] leading-[1.75] max-w-[58ch]" data-testid="home-hero-subline">
                II Eleven verbindet Vermögensverwaltung, internationale Strukturen, Immobilien und Vorsorgelösungen unter einer gemeinsamen Verantwortung – analytisch, diskret und auf Dauer angelegt.
              </motion.p>
              <motion.div variants={fadeUp} className="mt-10 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-[6px] h-12 px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold transition-colors duration-200" data-testid="home-hero-primary-cta">
                  <Link to="/anfrage/erstgespraech">Persönliches Erstgespräch vereinbaren <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-[6px] h-12 px-6 border-[#111318]/25 text-[#111318] bg-transparent hover:bg-[#F6F7F8] hover:border-[#111318] hover:text-[#111318] transition-colors duration-200" data-testid="home-hero-secondary-cta">
                  <Link to="/leistungen">Leistungsbereiche entdecken</Link>
                </Button>
              </motion.div>
            </motion.div>
            <div className="lg:col-span-6 hidden lg:block">
              <div className="relative overflow-hidden">
                <img src={IMG_HERO} alt="II Eleven Gruppe – institutionelle Vermögensverwaltung" className="w-full h-[520px] object-cover" loading="eager" decoding="async" data-testid="home-hero-image" />
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-[#D7DADF] pt-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#E8241B]">Vier Bereiche, eine Verantwortung</p>
                <p className="text-xs text-[#6B7280]">Family Office · Real Estate · Maklerservice · Development</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. POSITIONIERUNG */}
      <section className="bg-[#F6F7F8] border-t border-[#D7DADF] py-14 sm:py-24" data-testid="home-positionierung">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <SectionHeading
              eyebrow="Die Gruppe"
              title="Ein Ansprechpartner für Struktur, Immobilien, Finanzierung und Vorsorge"
              subtitle="Vermögensfragen enden selten an Produktgrenzen. Deshalb arbeiten die Bereiche der II Eleven Gruppe nicht nebeneinander, sondern ineinander – koordiniert von einem festen Ansprechpartner, der Ihre Gesamtsituation kennt."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              {[
                { icon: Scale, title: "Analytisch", text: "Am Anfang steht die Analyse Ihrer Situation – nicht ein Produkt." },
                { icon: Eye, title: "Transparent", text: "Dokumentierte Empfehlungen, offengelegte Kosten, klare Grenzen." },
                { icon: Globe2, title: "International", text: "Strukturen dort, wo sie für Ihre Ziele sinnvoll sind – Beratung in Stuttgart." },
                { icon: Handshake, title: "Langfristig", text: "Begleitung über Jahre statt Abschluss und Abschied." },
              ].map((w) => (
                <Reveal key={w.title} className="pt-5 border-t border-[#111318]/20">
                  <w.icon className="h-5 w-5 text-[#E8241B]" strokeWidth={1.75} />
                  <h4 className="mt-3.5 text-sm font-semibold text-[#0E1116]">{w.title}</h4>
                  <p className="mt-1.5 text-sm text-[#3B4452] leading-relaxed">{w.text}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. DIE VIER LEISTUNGSBEREICHE */}
      <section className="bg-white py-14 sm:py-24" data-testid="home-bereiche">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Vier Bereiche, eine Verantwortung"
            title="Die Leistungsbereiche der II Eleven Gruppe"
            subtitle="Jeder Bereich steht für sich – zusammen ergeben sie ein integriertes Ganzes."
          />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
            {BEREICHE.map((b) => (
              <Reveal key={b.title} className="h-full">
                <Link
                  to={b.to}
                  className="group flex h-full flex-col pt-6 border-t-2 border-[#111318]/15 hover:border-[#FF3C20] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF3C20]/40"
                  data-testid={b.testid}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono-num text-xs font-semibold text-[#E8241B]">{b.kicker}</span>
                    <b.icon className="h-5 w-5 text-[#111318]" strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#0E1116] leading-snug">{b.title}</h3>
                  <p className="mt-3 text-sm text-[#3B4452] leading-relaxed flex-1">{b.text}</p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#111318] group-hover:text-[#E8241B] transition-colors duration-200">
                    {b.cta} <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4. BERATER FINDEN (PLZ-Suche) */}
      <section className="bg-[#F6F7F8] border-y border-[#D7DADF] py-14 sm:py-20" data-testid="home-berater-finden">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <Reveal className="lg:col-span-6">
              <div className="flex items-center gap-3">
                <span className="h-px w-10 bg-[#FF3C20]" />
                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#6B7280]">Berater finden</p>
              </div>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-[-0.02em] text-[#0E1116]" data-testid="home-berater-finden-headline">
                Persönliche Beratung in Ihrer Region.
              </h2>
              <p className="mt-4 text-sm sm:text-base text-[#3B4452] leading-[1.75] max-w-[58ch]">
                Die II Eleven Gruppe betreut Mandanten deutschlandweit. Geben Sie Ihre Postleitzahl ein – wir ordnen Ihre Anfrage dem passenden Ansprechpartner in Ihrer Region zu und melden uns persönlich bei Ihnen.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 max-w-lg">
                <div className="flex-1">
                  <Input
                    value={plz}
                    onChange={(e) => { setPlz(e.target.value.replace(/\D/g, "").slice(0, 5)); setPlzError(false); }}
                    onKeyDown={(e) => { if (e.key === "Enter") goBeraterFinden(); }}
                    placeholder="Ihre Postleitzahl, z. B. 70173"
                    inputMode="numeric"
                    aria-label="Postleitzahl"
                    className={`h-12 rounded-[6px] bg-white border-[#C9CED6] text-[#0E1116] placeholder:text-[#6B7280] focus-visible:ring-[#FF3C20]/25 focus-visible:border-[#FF3C20] ${plzError ? "border-destructive" : ""}`}
                    data-testid="home-plz-input"
                  />
                  {plzError && <p className="mt-1.5 text-xs text-[#E8241B]" data-testid="home-plz-error">Bitte geben Sie eine gültige 5-stellige Postleitzahl ein.</p>}
                </div>
                <Button
                  className="h-12 rounded-[6px] px-6 bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold shrink-0 transition-colors duration-200"
                  onClick={goBeraterFinden}
                  data-testid="home-berater-finden-button"
                >
                  <MapPin className="h-4 w-4 mr-1.5" />
                  Berater finden
                </Button>
              </div>
            </Reveal>
            <div className="lg:col-span-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-8 lg:pt-2">
                {[
                  { icon: MapPin, title: "Regionale Zuordnung", text: "Ihre Anfrage wird anhand Ihrer Postleitzahl dem passenden Ansprechpartner zugeordnet." },
                  { icon: UserRound, title: "Ein fester Ansprechpartner", text: "Sie sprechen nicht mit einer Hotline, sondern mit einem persönlichen Ansprechpartner der Gruppe." },
                  { icon: Clock4, title: "Persönliche Rückmeldung", text: "In der Regel erhalten Sie innerhalb eines Werktages eine persönliche Rückmeldung." },
                ].map((s, i) => (
                  <Reveal key={s.title} className="pt-5 border-t border-[#111318]/20" data-testid={`home-berater-vorteil-${i + 1}`}>
                    <s.icon className="h-[18px] w-[18px] text-[#E8241B]" strokeWidth={1.75} />
                    <p className="mt-3 text-sm font-semibold text-[#0E1116]">{s.title}</p>
                    <p className="mt-1.5 text-xs text-[#3B4452] leading-relaxed">{s.text}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. FÜR WEN WIR ARBEITEN */}
      <section className="bg-white py-14 sm:py-24" data-testid="home-zielgruppen">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Für wen wir arbeiten"
            title="Für Menschen, die Entscheidungen mit Substanz treffen"
            subtitle="Unsere Mandanten verbindet weniger die Höhe des Vermögens als der Anspruch: verstehen, bevor entschieden wird."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {[
              { icon: Briefcase, title: "Unternehmer", text: "Trennung von Betriebs- und Privatvermögen, Strukturierung und Nachfolge." },
              { icon: LineChart, title: "Investoren & Anleger", text: "Diversifikation über Anlageklassen, Immobilien und Rechtsordnungen." },
              { icon: Users2, title: "Familien", text: "Vorsorge ordnen, Vermögen schützen, klare Regelungen für morgen." },
              { icon: ClipboardCheck, title: "Vertragsinhaber", text: "Bestehende Lebens- und Rentenversicherungen prüfen und optimieren." },
            ].map((z, i) => (
              <Reveal key={z.title} className="pt-6 border-t border-[#111318]/20" data-testid={`home-zielgruppe-${i + 1}`}>
                <z.icon className="h-5 w-5 text-[#E8241B]" strokeWidth={1.75} />
                <h4 className="mt-4 text-base font-semibold text-[#0E1116]">{z.title}</h4>
                <p className="mt-2 text-sm text-[#3B4452] leading-relaxed">{z.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 6. WIE WIR ARBEITEN */}
      <section className="dark-section noise-overlay py-14 sm:py-24" data-testid="home-arbeitsweise">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
            <SectionHeading
              dark
              eyebrow="Wie wir arbeiten"
              title="Grundsätze, die nicht verhandelbar sind"
              subtitle="Verantwortung zeigt sich nicht in Worten, sondern in der Architektur der Zusammenarbeit."
            />
            <div>
              {[
                { icon: Scale, title: "Analyse vor Produkt", text: "Jede Zusammenarbeit beginnt mit der Analyse Ihrer Situation – Lösungen folgen daraus, nicht umgekehrt." },
                { icon: ShieldCheck, title: "Getrennte Verwahrung", text: "Kundengelder fließen nie an Eleven. Kapital liegt in regulierten Strukturen – als Sondervermögen, im eigenen Depot einsehbar." },
                { icon: ClipboardCheck, title: "Dokumentierte Empfehlungen", text: "Jede Empfehlung wird schriftlich festgehalten – inklusive Risiken, Kosten und Grenzen. Kein Strukturvertrieb, kein Produktdruck." },
                { icon: Handshake, title: "Zusammenarbeit mit Ihren Beratern", text: "Steuerberater, Notare und externe Spezialisten werden ausdrücklich einbezogen – Ihre Entscheidung bleibt bei Ihnen." },
              ].map((g, i) => (
                <Reveal key={g.title} className={`flex gap-5 py-6 ${i > 0 ? "border-t border-white/12" : ""}`} data-testid={`home-grundsatz-${i + 1}`}>
                  <g.icon className="h-5 w-5 text-[#FF3C20] shrink-0 mt-0.5" strokeWidth={1.75} />
                  <div>
                    <p className="text-sm font-semibold text-white">{g.title}</p>
                    <p className="mt-1.5 text-sm text-white/55 leading-relaxed">{g.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 7. ZAHLEN & FAKTEN */}
      <section className="bg-white py-14 sm:py-24" data-testid="home-kpis">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Zahlen & Fakten" title="Die Gruppe in Zahlen" />
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {(site.kpis || []).map((k, i) => (
              <Reveal key={i} className="pt-6 border-t border-[#111318]/20" data-testid={`home-kpi-${i + 1}`}>
                <p className={`font-display text-4xl sm:text-5xl font-bold tracking-[-0.02em] ${i === 0 ? "text-[#FF3C20]" : "text-[#0E1116]"}`}>{k.value}</p>
                <p className="mt-3 text-xs sm:text-sm text-[#3B4452] leading-snug max-w-[24ch]">{k.label_de}</p>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-[11px] text-[#9AA1AB]">Stand: 2026 · Angaben der II Eleven Gruppe</p>
        </div>
      </section>

      {/* 8. PROZESS */}
      <section className="bg-[#F6F7F8] border-t border-[#D7DADF] py-14 sm:py-24" data-testid="home-prozess">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Zusammenarbeit"
            title="So beginnt eine Zusammenarbeit mit II Eleven"
            subtitle="Fünf Schritte – in jedem entscheiden Sie, ob es weitergeht."
          />
          <div className="mt-14">
            <ProcessTimeline steps={prozess} />
          </div>
        </div>
      </section>

      {/* 9. RISIKOBEWUSSTE KOMMUNIKATION */}
      <section className="bg-white py-14 sm:py-24" data-testid="home-risikohinweis">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Transparenz"
            title="Was wir bewusst nicht versprechen"
            subtitle="Kapitalanlagen, Immobilien und Vorsorgelösungen sind mit Risiken verbunden. Wer Ihnen Garantien verspricht, verkauft – wir beraten."
          />
          <div className="mt-8">
            <ComplianceNote>
              Alle Inhalte dieser Website dienen der allgemeinen Information und stellen keine Anlage-, Steuer- oder Rechtsberatung dar. Renditen, Konditionen und steuerliche Effekte hängen von Ihrer individuellen Situation ab und werden ausschließlich im persönlichen Gespräch nach individueller Prüfung besprochen. Kapitalanlagen unterliegen Wertschwankungen; Immobilien sind langfristige, nicht kurzfristig liquidierbare Anlagen. Ergebnisse der Vergangenheit sind kein verlässlicher Indikator für die Zukunft.
            </ComplianceNote>
          </div>
        </div>
      </section>

      {/* 10. CTA */}
      <CTASection
        title="Beginnen wir mit einem Gespräch – nicht mit einem Produkt."
        subtitle="Im persönlichen Erstgespräch verstehen wir Ihre Situation und zeigen, welcher Bereich der Gruppe für Sie der richtige Einstieg ist. Kostenfrei, diskret und unverbindlich."
        primaryLabel="Persönliches Erstgespräch vereinbaren"
        primaryTo="/anfrage/erstgespraech"
        secondaryLabel="Termin online buchen"
        secondaryTo="/termin"
      />
    </div>
  );
}
